import {
  useAdminFeatures,
  useFeatureAdminActions,
  useFeaturePermissions,
  useTenantTypeFeatureMatrix
} from '@lib/features/useFeatureAdmin'
import type { AdminFeature, FeatureFormValues } from '@lib/features/types'
import {
  DEFAULT_TENANT_TYPE_TAB,
  TENANT_TYPES,
  getTenantTypeLabel,
  tabValueToTenantType,
  tenantTypeToTabValue
} from '@lib/tenantTypes'
import { Alert, Grid, Stack, Tabs, Text } from '@mantine/core'
import { H1 } from '@packages/ui'
import { useMemo, useState } from 'react'
import { mutate } from 'swr'
import {
  FeaturePermissionPanel,
  buildFeaturesWithPermissions
} from './FeaturePermissionPanel'
import { IcpTypeSidebar } from './IcpTypeSidebar'
import { SystemRolesSection } from './SystemRolesSection'

export const FeaturesAdministration = () => {
  const { data: features = [], isLoading, error } = useAdminFeatures()
  const {
    data: permissionRows = [],
    isLoading: permissionsLoading,
    error: permissionsError
  } = useFeaturePermissions()
  const {
    data: matrixRows = [],
    isLoading: matrixLoading,
    error: matrixError
  } = useTenantTypeFeatureMatrix()
  const {
    upsertFeature,
    deleteFeature,
    createPermission,
    upsertPermission,
    setTenantTypeFeatures
  } = useFeatureAdminActions()

  const [savingType, setSavingType] = useState<string | null>(null)
  const [typeDrafts, setTypeDrafts] = useState<Record<string, Set<string>>>({})

  const matrixByType = useMemo(() => {
    const map: Record<string, Map<string, boolean>> = {}
    for (const row of matrixRows) {
      if (!map[row.tenant_type]) {
        map[row.tenant_type] = new Map()
      }
      map[row.tenant_type].set(row.feature_key, row.enabled)
    }
    return map
  }, [matrixRows])

  const activeTypeKeys = useMemo(() => {
    const fromData = [...new Set(matrixRows.map((r) => r.tenant_type))]
    const known = TENANT_TYPES.map((t) => t.value)
    return [...new Set([...known, ...fromData])]
  }, [matrixRows])

  const [selectedTabValue, setSelectedTabValue] = useState(() =>
    tenantTypeToTabValue(activeTypeKeys[0] ?? DEFAULT_TENANT_TYPE_TAB)
  )

  const selectedTenantType = tabValueToTenantType(selectedTabValue)

  const getEnabledKeysForType = (tenantType: string): Set<string> => {
    if (typeDrafts[tenantType]) {
      return typeDrafts[tenantType]
    }
    const map = matrixByType[tenantType]
    const enabled = new Set<string>()
    if (map) {
      for (const [key, on] of map.entries()) {
        if (on) enabled.add(key)
      }
    }
    return enabled
  }

  const toggleTypeFeature = (
    tenantType: string,
    featureKey: string,
    enabled: boolean
  ) => {
    const current = new Set(getEnabledKeysForType(tenantType))
    if (enabled) current.add(featureKey)
    else current.delete(featureKey)
    setTypeDrafts((prev) => ({ ...prev, [tenantType]: current }))
  }

  const saveTenantType = async (tenantType: string) => {
    setSavingType(tenantType)
    try {
      const keys = [...getEnabledKeysForType(tenantType)]
      await setTenantTypeFeatures(tenantType, keys)
      setTypeDrafts((prev) => {
        const next = { ...prev }
        delete next[tenantType]
        return next
      })
      await mutate('admin-tenant-type-feature-matrix')
    } finally {
      setSavingType(null)
    }
  }

  const refreshCatalog = async () => {
    await Promise.all([
      mutate('admin-features'),
      mutate('admin-feature-permissions'),
      mutate('admin-tenant-type-feature-matrix')
    ])
  }

  const handleUpsertFeature = async (
    values: FeatureFormValues,
    editingId: string | null
  ) => {
    await upsertFeature({
      key: values.key.trim(),
      name: values.name.trim(),
      description: values.description.trim() || null,
      sort_order: values.sort_order,
      disabled: values.disabled,
      feature_id: editingId
    })
    await refreshCatalog()
  }

  const handleDeleteFeature = async (feature: AdminFeature) => {
    await deleteFeature(feature.id)
    await refreshCatalog()
  }

  const handleCreatePermission = async (
    featureId: string,
    values: { name: string; description: string; disabled: boolean }
  ) => {
    await createPermission({
      name: values.name.trim(),
      feature_id: featureId,
      description: values.description.trim() || null,
      disabled: values.disabled
    })
    await refreshCatalog()
  }

  const handleUpdatePermission = async (
    permissionId: string,
    values: { description: string; disabled: boolean }
  ) => {
    await upsertPermission({
      permission_id: permissionId,
      description: values.description.trim() || null,
      disabled: values.disabled
    })
    await refreshCatalog()
  }

  const featuresWithPermissions = useMemo(
    () => buildFeaturesWithPermissions(features, permissionRows),
    [features, permissionRows]
  )

  const enabled = getEnabledKeysForType(selectedTenantType)
  const hasDraft = Boolean(typeDrafts[selectedTenantType])
  const loadError = error || matrixError || permissionsError

  return (
    <Stack gap="xl">
      <Stack gap="xs">
        <H1 className="text-white">Features, permissions &amp; roles</H1>
        <Text size="sm" c="dimmed">
          Layer 1 (ICP): tenant type defines module ceiling. Permissions belong
          to features. Tenant custom add-ons live on each tenant&apos;s Features
          tab. Role templates for Hub system roles are edited below.
        </Text>
      </Stack>

      {loadError && (
        <Alert color="red" title="Failed to load feature data">
          {loadError.message}
        </Alert>
      )}

      <Tabs defaultValue="icp" variant="outline">
        <Tabs.List>
          <Tabs.Tab value="icp">ICP &amp; catalog</Tabs.Tab>
          <Tabs.Tab value="system-roles">System roles</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="icp" pt="md">
          <Grid gap="lg">
            <Grid.Col span={{ base: 12, md: 3 }}>
              <IcpTypeSidebar
                activeTypeKeys={activeTypeKeys}
                selectedTabValue={selectedTabValue}
                onSelect={setSelectedTabValue}
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 9 }}>
              <FeaturePermissionPanel
                tenantTypeLabel={getTenantTypeLabel(selectedTenantType)}
                features={featuresWithPermissions}
                enabledKeys={enabled}
                hasDraft={hasDraft}
                saving={savingType === selectedTenantType}
                loading={isLoading || matrixLoading || permissionsLoading}
                onToggleFeature={(featureKey, on) =>
                  toggleTypeFeature(selectedTenantType, featureKey, on)
                }
                onSaveIcp={() => saveTenantType(selectedTenantType)}
                onDiscardIcp={() =>
                  setTypeDrafts((prev) => {
                    const next = { ...prev }
                    delete next[selectedTenantType]
                    return next
                  })
                }
                onUpsertFeature={handleUpsertFeature}
                onDeleteFeature={handleDeleteFeature}
                onCreatePermission={handleCreatePermission}
                onUpdatePermission={handleUpdatePermission}
              />
            </Grid.Col>
          </Grid>
        </Tabs.Panel>

        <Tabs.Panel value="system-roles" pt="md">
          <SystemRolesSection />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  )
}

export default FeaturesAdministration
